import {
  Box,
  Button,
  Center,
  Divider,
  Flex,
  Icon,
  IconButton,
  Select,
  Skeleton,
  Spacer,
  Spinner,
  TabPanel,
  Text,
  FormControl,
  FormLabel,
  Switch,
} from '@chakra-ui/react'
import { CalorieBurn, SoccerBall, StepArrow } from '../../components/icons/icons'

import { useEffect, useState } from 'react'
import { Capacitor } from '@capacitor/core'
import { requestHealthKitAuthorization, useHealthKitSync } from '../../hooks/useHealthKitSync'
import { TbRefresh, TbRoute } from 'react-icons/tb'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from 'react-query'
import { getUpcomingEvents } from '../../api/user/notifications/Notifications'
import { getHealthStats, putActivitySharing } from '../../api/user/profile/Profile'
import api from '../../api/api'
import EventDetailCard from '../../components/cards/EventDetailCard'
import InfinityGrid, { getInfinityPages } from '../../components/datagrid/InfinityGrid'
import StatsChart from '../../components/display/StatsChart'
import BlankState from '../../components/input/BlankState'
import OptionsSwitcher from '../../components/input/OptionsSwitcher'
import { getSkillLevelDisplay } from '../../functions/Capitalize'
import useMobile from '../../hooks/useMobile'

// const SKILL_LEVELS = ['Beginner', 'Intermediate', 'Intermediate High', 'Advanced', 'Advanced 4.5+', 'Mixed-Level']

// Helper function to convert date format from dd/MM/yyyy to MM/dd/yyyy
const convertDateFormat = (dateString: string): string => {
  // Check if the string matches dd/MM/yyyy format
  const ddMMyyyyPattern = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/
  const match = dateString.match(ddMMyyyyPattern)

  if (match) {
    const [, day, month, year] = match
    return `${month}/${day}/${year}`
  }

  // If it doesn't match the expected format, return as is
  return dateString
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  } else if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

export const StatsBox = ({
  games,
  heartRate,
  calories,
  steps,
  distance,
  isLoading,
}: {
  games: number
  heartRate: number
  calories: number
  distance: number
  steps: number
  isLoading?: boolean
}) => {
  // Convert meters to miles (1 meter = 0.000621371 miles)
  const distanceInMiles = (distance * 0.000621371).toFixed(1)
  console.debug(heartRate)

  return (
    <>
      <Flex borderRadius="10px" bgColor="#F5F7F9" p="10px 8px 10px 8px" justifyContent="space-around">
        <Box ml="5px" minW="60px" textAlign="center">
          <Center>
            <Icon w="18px" h="18px" viewBox="0 0 14 14">
              <SoccerBall />
            </Icon>
            <Skeleton isLoaded={!isLoading} ml="3px" h="20px">
              <Text ml="3px" fontWeight="500" color="#171C22" fontSize="lg" minW="20px" textAlign="left">
                {formatNumber(games ?? 0)}
              </Text>
            </Skeleton>
          </Center>
          <Center>
            <Text color="#7E8B9A" fontWeight="400" fontSize="md">
              Games
            </Text>
          </Center>
        </Box>
        <Center m="10px 0" borderWidth="1px">
          <Divider orientation="vertical" />
        </Center>
        <Box minW="60px" textAlign="center">
          <Center>
            <Icon w="18px" h="18px" viewBox="0 0 24 24" as={TbRoute} />
            <Skeleton isLoaded={!isLoading} ml="3px" h="20px">
              <Text ml="3px" fontWeight="500" color="#171C22" fontSize="lg" minW="30px" textAlign="left">
                {distanceInMiles}mi
              </Text>
            </Skeleton>
          </Center>
          <Center>
            <Text color="#7E8B9A" fontWeight="400" fontSize="md">
              Distance
            </Text>
          </Center>
        </Box>
        <Center m="10px 0" borderWidth="1px">
          <Divider orientation="vertical" />
        </Center>
        <Box minW="60px" textAlign="center">
          <Center>
            <Icon w="18px" h="18px" viewBox="0 0 18 18" fill="red">
              <CalorieBurn />
            </Icon>
            <Skeleton isLoaded={!isLoading} ml="3px" h="20px">
              <Text ml="2px" fontWeight="500" color="#171C22" fontSize="lg" minW="20px" textAlign="left">
                {formatNumber(calories)}
              </Text>
            </Skeleton>
          </Center>
          <Center>
            <Text color="#7E8B9A" fontWeight="400" fontSize="md">
              Calories
            </Text>
          </Center>
        </Box>
        <Center m="10px 0" borderWidth="1px">
          <Divider orientation="vertical" />
        </Center>
        <Box mr="5px" minW="60px" textAlign="center">
          <Center>
            <Icon w="18px" h="18px" viewBox="0 0 18 18" fill="red">
              <StepArrow />
            </Icon>
            <Skeleton isLoaded={!isLoading} ml="3px" h="20px">
              <Text ml="2px" fontWeight="500" color="#171C22" fontSize="lg" minW="20px" textAlign="left">
                {formatNumber(steps)}
              </Text>
            </Skeleton>
          </Center>
          <Center>
            <Text color="#7E8B9A" fontWeight="400" fontSize="md">
              Steps
            </Text>
          </Center>
        </Box>
      </Flex>
    </>
  )
}

const OFFSET_LABELS = [
  ['Last Week', 'This Week'],
  ['Last Month', 'This Month'],
  ['Last Year', 'This Year'],
  ['All Time'],
]

const formatRelativeSince = (timestamp: number | null): string => {
  if (!timestamp) return 'Never synced'
  const diffSec = Math.max(0, Math.floor((Date.now() - timestamp) / 1000))
  if (diffSec < 45) return 'Just synced'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `Synced ${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `Synced ${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  return `Synced ${diffDay}d ago`
}

/**
 * Inline metadata row showing sync state. Sits directly on the parent surface —
 * no pill, no chrome — so it visually recedes when nothing is wrong and only
 * gets loud (red dot + red text) when sync actually fails. Only renders when
 * HealthKit is connected; the not-connected case is handled by the explicit
 * <ConnectHealthKitButton /> in the blank state, where a real CTA belongs.
 */
const SyncStatusRow = () => {
  const { status, lastSyncedAt, lastError, syncNow, isSupported, isConnected } =
    useHealthKitSync()
  // Re-render every 30s so "Synced Xm ago" stays fresh.
  const [, setTick] = useState(0)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30 * 1000)
    return () => clearInterval(id)
  }, [])

  if (!isSupported || !isConnected) return null

  const isSyncing = status === 'syncing'
  const failed = status === 'error' && !!lastError

  const dotColor = failed ? '#E53E3E' : lastSyncedAt ? '#38A169' : '#CBD5E0'
  const textColor = failed ? '#C53030' : '#7E8B9A'
  const label = failed
    ? 'Sync failed'
    : isSyncing
    ? 'Syncing…'
    : lastSyncedAt
    ? formatRelativeSince(lastSyncedAt)
    : 'Not synced yet'

  return (
    <Flex align="center" justify="space-between" mb="14px" minH="32px">
      <Flex align="center" gap="8px">
        {isSyncing ? (
          <Spinner size="xs" color="#7E8B9A" speed="0.8s" thickness="2px" />
        ) : (
          <Box w="6px" h="6px" borderRadius="full" bgColor={dotColor} flexShrink={0} />
        )}
        <Text fontSize="sm" fontWeight="500" color={textColor}>
          {label}
        </Text>
      </Flex>
      {!isSyncing && (
        <IconButton
          aria-label={failed ? 'Retry sync' : 'Sync now'}
          icon={<Icon as={TbRefresh} w="16px" h="16px" />}
          variant="ghost"
          color={failed ? '#C53030' : '#171C22'}
          minW="32px"
          h="32px"
          borderRadius="8px"
          onClick={() => syncNow()}
          _hover={{ bg: '#F5F7F9' }}
          _active={{ bg: '#EDEFF2' }}
        />
      )}
    </Flex>
  )
}

/**
 * Solid CTA shown in the BlankState when HealthKit isn't connected yet.
 * Matches the visual language of HealthKitPromptModal (black solid, 10px radius)
 * so the connection ask feels consistent regardless of entry point.
 */
const ConnectHealthKitButton = () => {
  const queryClient = useQueryClient()
  const [isAuthorizing, setIsAuthorizing] = useState(false)
  const updateProfile = useMutation(
    (data: { healthkit_connected?: boolean }) => api.put('member/myprofile/', data),
    { onSuccess: () => queryClient.invalidateQueries(['auth']) }
  )

  if (!Capacitor.isNativePlatform()) return null

  const handleConnect = async () => {
    setIsAuthorizing(true)
    try {
      const success = await requestHealthKitAuthorization()
      if (success) {
        updateProfile.mutate({ healthkit_connected: true })
      }
    } finally {
      setIsAuthorizing(false)
    }
  }

  return (
    <Button
      w="100%"
      maxW="320px"
      bg="black"
      color="white"
      borderRadius="10px"
      fontSize="15px"
      fontWeight="600"
      py="12px"
      mt="16px"
      _hover={{ bg: 'gray.800' }}
      _active={{ bg: 'gray.700' }}
      isLoading={isAuthorizing || updateProfile.isLoading}
      onClick={handleConnect}
    >
      Connect HealthKit
    </Button>
  )
}

const UserProfileActivityPage = ({
  hasHealthkit,
  isExternal,
  userId,
  isSharingActivity,
}: {
  hasHealthkit: boolean
  isExternal: boolean | undefined
  userId?: string | undefined
  isSharingActivity?: boolean
}) => {
  const isTablet = useMobile(950)
  const isMobile = useMobile(750)
  const [period, setPeriod] = useState(0)
  const [offset, setOffset] = useState(0)
  const [healthkitConnected, setHealthkitConnected] = useState(false)

  const [activitySharing, setActivitySharing] = useState(isSharingActivity ?? false)

  useEffect(() => {
    setActivitySharing(isSharingActivity ?? false)
  }, [isSharingActivity])

  const updateActivitySharingMutation = useMutation(putActivitySharing, {
    onSuccess: (data) => {
      console.log('Activity sharing updated successfully:', data)
    },
    onError: (error) => {
      console.error('Error updating activity sharing:', error)
      // Optionally revert the toggle state if the update fails
      setActivitySharing((prev) => !prev)
    },
  })

  const handleToggleActivitySharing = async () => {
    try {
      const newValue = !activitySharing
      setActivitySharing(newValue)
      updateActivitySharingMutation.mutate({ activity_data_shared: newValue })
    } catch (error) {
      console.error('Error updating activity sharing:', error)
    }
  }

  console.log(hasHealthkit)
  useEffect(() => {
    setOffset(0)
  }, [period])

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return
    requestHealthKitAuthorization().then((success) => {
      setHealthkitConnected(success)
    })
  }, [])
  const periodResults = ['W', 'M', 'Y', 'All']

  const activityStats = useQuery(['getHealthStats', period, offset, isExternal, userId], () => {
    const payload: any = {
      interval: periodResults[period],
      offset: offset,
    }
    if (isExternal && userId) {
      payload.user_id = userId
    }
    return getHealthStats(payload)
  })

  const activityEvents = useInfiniteQuery(
    ['getHealthEvents'],
    ({ pageParam = 1 }) =>
      getUpcomingEvents({
        has_healthkit_data: true,
        page: pageParam,
        page_size: 10,
      }),
    {
      getNextPageParam: (lastPage) => {
        if (!lastPage?.hasNext) {
          return null
        }
        return lastPage.nextCursor
      },
    }
  )

  const width = isTablet ? 'calc(100% - 30px)' : isMobile ? '100%' : '600px'

  return (
    <TabPanel p="0">
      <Center flexDir="column">
        {hasHealthkit ? (
          <>
            <Box
              p={isMobile ? '' : '24px'}
              border={isMobile ? '' : '0.5px solid'}
              borderColor="#00000033"
              borderRadius="10px"
              boxShadow={!isMobile ? '0px 12.914606094360352px 86.71235656738281px 0px #2B2C2D14' : ''}
              bgColor={!isMobile ? 'white' : ''}
              margin="auto"
              w={width}
              maxWidth="600px"
            >
              {!isMobile && (
                <Flex alignItems={'baseline'} flexDir="row" justifyContent="space-between" mb="18px" w="100%">
                  <Text fontSize="lg" fontWeight="500" color="#171C22">
                    Activity
                  </Text>
                  {!isExternal && (
                    <FormControl display="flex" alignItems="center" justifyContent={'right'}>
                      <FormLabel htmlFor="share-activity" fontWeight="500" color="#171C22" fontSize="md" mb="0">
                        Share activity
                      </FormLabel>
                      <Switch
                        id="share-activity"
                        isChecked={activitySharing}
                        onChange={handleToggleActivitySharing}
                        isDisabled={typeof isSharingActivity === 'undefined'}
                      />
                    </FormControl>
                  )}
                </Flex>
              )}
              {!isExternal && <SyncStatusRow />}
              <OptionsSwitcher
                activeOption={period}
                setActiveOption={setPeriod}
                height={40}
                components={[
                  <Text fontSize="md" fontWeight="500" color={period == 0 ? 'white' : '#171C22'} textAlign="center">
                    W
                  </Text>,
                  <Text fontSize="md" fontWeight="500" color={period == 1 ? 'white' : '#171C22'} textAlign="center">
                    M
                  </Text>,
                  <Text fontSize="md" fontWeight="500" color={period == 2 ? 'white' : '#171C22'} textAlign="center">
                    Y
                  </Text>,
                  <Text fontSize="md" fontWeight="500" color={period == 3 ? 'white' : '#171C22'} textAlign="center">
                    All
                  </Text>,
                ]}
              />
              {period != 3 ? (
                <Flex flexDir={'row'}>
                  <Select
                    mt="18px"
                    mb="12px"
                    fontWeight="500"
                    fontSize="sm"
                    w={isMobile ? '200px' : '150px'}
                    value={offset}
                    onChange={(e) => setOffset(parseInt(e.target.value))}
                    borderWidth="0"
                    color="#171C22"
                    h="24px"
                  >
                    {OFFSET_LABELS[period].map((label, i) => {
                      return (
                        <option key={i} value={OFFSET_LABELS[period].length - i - 1}>
                          {label}
                        </option>
                      )
                    })}
                  </Select>
                  {isMobile && !isExternal && (
                    <FormControl display="flex" alignItems="center" justifyContent={'right'}>
                      <FormLabel htmlFor="share-activity" fontWeight="500" color="#171C22" fontSize="md" mb="0">
                        Share activity
                      </FormLabel>
                      <Switch
                        id="share-activity"
                        isChecked={activitySharing}
                        onChange={handleToggleActivitySharing}
                        isDisabled={typeof isSharingActivity === 'undefined'}
                      />
                    </FormControl>
                  )}
                </Flex>
              ) : (
                <Spacer h="55.5px" />
              )}
              <StatsBox
                distance={Math.round(activityStats.data?.total.distance) ?? 0}
                games={Math.round(activityStats.data?.total.count) ?? 0}
                heartRate={Math.round(activityStats.data?.total.distance) ?? 0}
                calories={Math.round(activityStats.data?.total.calories) ?? 0}
                steps={Math.round(activityStats.data?.total.steps) ?? 0}
                isLoading={activityStats.isLoading}
              />
              <Text mt="8px" mb="12px">
                <Text as="span" fontSize="lg">
                  Calories
                </Text>
                {/* <Text as="span" fontSize="lg">
                  Burnt
                </Text> */}
              </Text>
              <StatsChart
                data={
                  activityStats.data?.graph.map((item: any) => {
                    return {
                      name: convertDateFormat(item.label),
                      uv: item.calories >= 1 ? item.calories : 0,
                    }
                  }) ?? []
                }
              />
            </Box>
            {!isExternal && (
              <Box margin="auto" w={width}>
                <Text mt="24px" mb="12px" color="#171C22" fontSize="lg">
                  Recent Activity
                </Text>
                <Box margin="auto" mb="10px">
                  {activityEvents.isLoading &&
                    [1, 1, 1, 1, 1, 1, 1, 1, 1, 1, 1].map((_, i) => {
                      return <Skeleton key={`skeleton-${i}`} height="384px" w={width} borderRadius="10px" mb="10px" />
                    })}
                  <InfinityGrid
                    isFetchingNextPage={activityEvents.isFetchingNextPage}
                    hasNextPage={activityEvents.hasNextPage || false}
                    isLoading={activityEvents.isLoading}
                    onLoadMore={() => {
                      activityEvents.fetchNextPage()
                    }}
                  >
                    {getInfinityPages(activityEvents, 'events').map((event) => {
                      return (
                        <>
                          <EventDetailCard
                            key={event.id}
                            w={width}
                            status="activity"
                            id={event.id}
                            time={new Date(event.start_time)}
                            type={event.type}
                            secondary_type={event.secondary_type}
                            court={event.court}
                            level={getSkillLevelDisplay(event.skill_level)}
                            coaches={event.coaches}
                            host={event.host}
                            players={event.participants}
                            description={event.description}
                            price={event.price}
                            duration={event.duration}
                            distance={Math.round(event.healthkit_data.distance ?? 0)}
                            onOpenWaitlist={() => {}}
                            location={event.venue.name}
                            calories={Math.round(event.healthkit_data.calories ?? 0)}
                            steps={event.healthkit_data.steps ?? 0}
                            heartRate={Math.round((event.healthkit_data.distance ?? 0) * 0.000621371)}
                          />
                          <Spacer h="24px" />
                        </>
                      )
                    })}
                  </InfinityGrid>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box p="20px" w={width} maxWidth="600px" margin="auto">
            {/* When the user IS connected but the data hasn't landed yet, surface
                the sync state so they know the app is actively trying. The row
                self-hides when not connected — the explicit Connect CTA below
                handles that case more usefully than a disabled "Sync now". */}
            {!isExternal && <SyncStatusRow />}
            <BlankState
              image="/blank/No upcoming events 2.svg"
              title="No Healthkit Data"
              description={
                healthkitConnected
                  ? 'You have connected your Healthkit data but there is no data available.'
                  : activityEvents.data?.pages[0]?.count > 0
                  ? 'You have played games but need to connect Healthkit data to see the stats.'
                  : 'You have not connected your Healthkit data to your account.'
              }
            />
            {!isExternal && !healthkitConnected && (
              <Center>
                <ConnectHealthKitButton />
              </Center>
            )}
          </Box>
        )}
      </Center>
    </TabPanel>
  )
}

export default UserProfileActivityPage
